"use client";

import { toast } from "sonner";
import { useContext, useEffect, useRef, useState } from "react";
import { Clock4Icon, UserPlusIcon, XIcon } from "lucide-react";
import { useRouter } from "@bprogress/next";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { PictureAsset } from "@/components/common/picture-asset";
import {
  BANNER_BACKGROUND,
  BANNER_PERSON,
  PracticeExamRoundSteps,
} from "@/components/exams/practice-exam/practice-exam-banner-shell";
import { PracticeExamDialog } from "@/components/exams/practice-exam/practice-exam-dialog";
import { CountdownTimer } from "@/components/common/countdown-timer";
import { getTimeForExam } from "@/components/exams/exam-question/components/exam-timer/manual-exam-timer/service";
import { clientPost } from "@/service/client-api/client-api";
import { logEvent } from "@/service/logs/log";
import { ResourceContext } from "@/contexts/resource-context";
import { getPracticeExamInfo } from "@/service/exams/practice-exam-info";

const REFRESH_COOLDOWN = 10_000;

// tło pionowej karty mobilnej (poniżej sm) — osobna grafika niż baner desktopowy
const MOBILE_BACKGROUND =
  "https://cdn.remedium.md/image/common/2026/08/03/10/tlo3-png-6a7053a5d0fbd";

/**
 * Baner próbnego LEK-u na pulpicie (kampania banerowa, klucz `probny-lek-2-2026`).
 *
 * Własny layout (bez paska sekcji i licznika uczestników — na pulpicie baner stoi sam i jest
 * zamykalny), żeby nie obudowywać propsami skorupy ze strony egzaminu. Wspólne z tamtym banerem
 * zostają tylko grafiki i wskaźnik rund. Treść zapowiada całą edycję, a nie pojedynczą rundę.
 */
export const PracticeExamHomeBanner = ({
  practiceExamStatus,
  closeBanner,
}: {
  practiceExamStatus: ExamPracticeExamStatusVM;
  closeBanner?: () => void;
}) => {
  const {
    startDate,
    endDate,
    positionInGroup,
    groupSize,
    isFinishedByUser,
    isSubscribed,
  } = practiceExamStatus;
  const { logView } = useContext(ResourceContext);
  const router = useRouter();
  const info = getPracticeExamInfo(practiceExamStatus);

  const lastRefreshRef = useRef(0);
  const [subscribing, setSubscribing] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const isActive = Boolean(
    startDate &&
      endDate &&
      new Date() >= new Date(startDate) &&
      new Date() <= new Date(endDate),
  );

  const [timeLeft, setTimeLeft] = useState(
    getTimeForExam(new Date((isActive ? endDate : startDate) || new Date())),
  );

  useEffect(() => {
    if (!startDate || !endDate) return;

    const updateTimeLeft = () => {
      const timeLeft = getTimeForExam(
        new Date((isActive ? endDate : startDate) || new Date()),
      );

      const totalSecondsLeft =
        (timeLeft.days ?? 0) * 86400 +
        timeLeft.hours * 3600 +
        timeLeft.minutes * 60 +
        timeLeft.seconds;

      // koniec odliczania — przeładowanie danych rundy (z cooldownem, żeby nie zapętlić)
      if (totalSecondsLeft <= 0) {
        const now = Date.now();

        if (now - lastRefreshRef.current > REFRESH_COOLDOWN) {
          lastRefreshRef.current = now;
          router.refresh();
        }

        return;
      }

      setTimeLeft(timeLeft);
    };

    const intervalId = setInterval(updateTimeLeft, 1000);

    return () => clearInterval(intervalId);
  }, [startDate, endDate, isActive, router]);

  // bez zaplanowanej rundy nie ma czego zapowiadać — kampania po prostu się nie pokazuje
  if (!startDate || !endDate || !info) return null;

  // Baner zaprasza do zapisu, więc zapisanym nie ma co pokazywać. Bramka jest tu, a nie tylko
  // w doborze odbiorców kampanii: audiencję liczy się okresowo, a zapis dzieje się od razu —
  // bez tego user po kliknięciu „Zapisz się" widziałby to samo zaproszenie do kolejnego przeliczenia.
  if (isSubscribed) return null;

  const logButtonClick = (type: string) =>
    logEvent("home_banner_button_click", logView, {
      banner_type: "practice_exam",
      banner_button_type: type,
    });

  // Dialog otwiera się dopiero po udanym zapisie (wcześniej wisiał na triggerze, więc pokazywał
  // potwierdzenie także przy błędzie). Odświeżenie danych czeka na zamknięcie dialogu — inaczej
  // baner znikał (bramka isSubscribed) razem z dopiero co otwartym potwierdzeniem.
  async function subscribe() {
    if (subscribing) return;

    try {
      setSubscribing(true);
      await clientPost("exam.practice_exam.subscribe");
      logEvent("practice_exam_subscribe", logView);
      logButtonClick("sign up");
      setConfirmationOpen(true);
    } catch (error) {
      console.error("Subscription failed", error);
      toast.error("Nie udało się zapisać na próbny LEK. Spróbuj ponownie.");
    } finally {
      setSubscribing(false);
    }
  }

  const headline = isActive
    ? `Runda ${positionInGroup ?? 1}/${groupSize ?? 1} czeka na Ciebie`
    : `🚀 Próbny LEK start ${info.roundDateTimeShort}`;

  // na karcie mobilnej data jest w osobnej linii pod tytułem, więc nagłówek jej nie powtarza
  const mobileHeadline = isActive
    ? `Runda ${positionInGroup ?? 1}/${groupSize ?? 1} czeka na Ciebie`
    : "🚀 Próbny LEK";

  // „4 rundy · co sobotę · 8–29 sierpnia o 10:00" — zakres całej edycji, bo na pulpicie baner
  // zaprasza do udziału, a nie prowadzi przez pojedynczą rundę
  const metaPrefix = [info.roundsLabel, `co ${info.weekdayAccusative}`]
    .filter(Boolean)
    .join(" · ");

  const rounds = groupSize ?? 0;
  const currentRound = positionInGroup ?? 1;

  // Odliczanie znika tylko po oddaniu podejścia — wtedy zostaje przejście do wyników.
  const showCountdown = !isFinishedByUser;

  // krzyżyk kampanii — ten sam w wariancie mobilnym i desktopowym
  const renderCloseButton = () =>
    closeBanner && (
      <Button
        variant="neutral"
        size="icon"
        aria-label="Zamknij baner"
        onClick={closeBanner}
        className="absolute top-1 right-1 z-20 size-9 text-white/70 hover:text-white [&_path]:stroke-current"
      >
        <XIcon size={20} />
      </Button>
    );

  // Baner ma jedno zadanie — zapis na próbny LEK, więc CTA jest zawsze to samo. Po zamknięciu
  // potwierdzenia router.refresh() dociąga status z isSubscribed = true i baner znika (bramka wyżej).
  // Spinner w miejscu ikony, bo samo `disabled` na ciemnym przycisku było ledwo widoczne.
  const renderCta = () => (
    <Button variant="default" onClick={subscribe} disabled={subscribing}>
      {subscribing ? <Spinner size="small" /> : <UserPlusIcon />} Zapisz się
    </Button>
  );

  return (
    <>
      {/* jedna instancja na komponent — renderCta() woła się i w karcie mobilnej, i w banerze
          desktopowym, więc dialog stoi poza nim, żeby nie zdublował się na obu wariantach */}
      <PracticeExamDialog
        practiceExamStatus={practiceExamStatus}
        open={confirmationOpen}
        onOpenChange={setConfirmationOpen}
        onClose={() => router.refresh()}
      />
      {/* Poniżej sm szeroki baner się nie mieści — karta z projektu mobilnego (Figma 21087:65264):
          całe tło granatowe, treść u góry, postać z chipami przy dolnej krawędzi. Bez wskaźnika
          rund i licznika uczestników — w tym widoku ich nie ma. */}
      <section className="relative mt-6 mb-10 overflow-hidden rounded-2xl sm:hidden">
        {/* tło karty (656x760) — jednolity niebieski z rozświetleniem na środku, w docelowym
            kolorze, więc bez nakładki mix-blend. Granat marki zostaje pod spodem jako fallback,
            gdy grafika się nie wczyta. */}
        <span
          aria-hidden
          className="bg-remedium-blue pointer-events-none absolute inset-0"
        />
        <PictureAsset
          source={MOBILE_BACKGROUND}
          ext="png"
          alt=""
          wrapperClassName="pointer-events-none absolute inset-0"
          className="h-full w-full object-cover"
        />
        {renderCloseButton()}

        <div className="relative z-10 flex max-w-[288px] flex-col gap-2 px-5 pt-6">
          <p className="text-xl font-semibold tracking-[-0.4px] text-white">
            {mobileHeadline}
          </p>
          {/* trigger dialogu to <button>, który jako element flexa rozciągnąłby się na całą
              szerokość i wyśrodkował tekst — stąd wrapper trzymający go przy lewej krawędzi */}
          <div className="self-start text-left">
            <PracticeExamDialog practiceExamStatus={practiceExamStatus}>
              <span className="cursor-pointer text-sm leading-6 text-white hover:underline">
                {metaPrefix}
                {metaPrefix && " · "}
                <span className="font-bold">{info.roundsRangeLabel}</span>
              </span>
            </PracticeExamDialog>
          </div>
          <p className="text-sm leading-[22px] text-white">
            Sprawdź się w realnych warunkach. Przygotuj się, by podejść do
            egzaminu spokojniej.
          </p>
          {/* licznik i CTA w jednym wierszu — przycisk zajmuje resztę szerokości */}
          <div className="mt-2 flex items-center gap-3">
            {showCountdown && (
              <div className="flex shrink-0 items-center gap-1.5">
                <span className="flex items-center gap-2 text-sm font-medium whitespace-nowrap text-white/80">
                  <Clock4Icon size={16} className="shrink-0 opacity-80" />
                  {isActive ? "Czas:" : "Start za:"}
                </span>
                {/* wariant „default": dni jako jedno pole „5d", tak jak w projekcie mobilnym */}
                <CountdownTimer
                  timeLeft={timeLeft}
                  isActive={isActive}
                  className="text-white"
                />
              </div>
            )}
            <div className="min-w-0 flex-1 [&_button]:h-9 [&_button]:w-full [&>*]:w-full">
              {renderCta()}
            </div>
          </div>
        </div>

        {/* Postać z chipami przy dolnej krawędzi. Wysokość bloku = wysokość grafiki, żeby chipy
            nie wchodziły na wiersz z licznikiem i CTA. */}
        <div className="relative mt-4 h-[164px]">
          <PictureAsset
            source={BANNER_PERSON}
            ext="png"
            alt=""
            wrapperClassName="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2"
            className="h-[164px] w-auto max-w-none"
          />
        </div>
      </section>

      {/* Na pulpicie baner jest jednym z bloków strony (zwykłe marginesy) i nie ma paska sekcji
          ani licznika uczestników — stąd własny kontener zamiast skorupy ze strony egzaminu.
          Grafiki i wskaźnik rund są te same, żeby oba banery nie rozjechały się wizualnie. */}
      <div className="mt-6 mb-10 hidden sm:block">
        {/* O układzie decyduje szerokość samego banera (@container), nie viewportu — przy rozwiniętym
            menu bocznym baner na 1280 px ekranu ma tylko ~900 px i wersja z postacią się nie mieści. */}
        <div className="from-remedium-blue to-remedium-blue-light @960:px-10 @960:py-8 @container relative overflow-hidden rounded-2xl bg-gradient-to-r px-6 py-6">
          {/* tło banera; gradient pod spodem zostaje jako fallback, gdy grafika się nie wczyta */}
          <PictureAsset
            source={BANNER_BACKGROUND}
            ext="png"
            alt=""
            wrapperClassName="pointer-events-none absolute inset-0"
            className="h-full w-full object-cover"
          />
          {/* Postać z chipami — dekoracja. Wchodzi dopiero, gdy między kolumnami zostaje na nią
              miejsce; węziej nachodziłaby chipami na tekst z datami. */}
          <PictureAsset
            source={BANNER_PERSON}
            ext="png"
            alt=""
            wrapperClassName="pointer-events-none absolute top-[9%] left-1/2 hidden h-full -translate-x-1/2 @1088:block"
            className="h-full w-auto max-w-none"
          />
          {renderCloseButton()}
          <div className="@960:items-center relative z-10 flex items-end justify-between gap-6">
            <div className="@960:max-w-[42%] @1088:max-w-[36%] min-w-0">
              <p className="@960:text-2xl text-xl font-bold tracking-[-0.48px] text-white">
                {headline}
              </p>
              {/* Wariant wąski: zamiast opisu idzie tu opis edycji z prawej kolumny — inaczej
                  daty i licznik z CTA nie zmieszczą się obok siebie w jednej linii. */}
              <div className="@960:hidden mt-1">
                <PracticeExamDialog practiceExamStatus={practiceExamStatus}>
                  <span className="cursor-pointer text-sm text-white hover:underline">
                    {metaPrefix}
                    {metaPrefix && " · "}
                    <span className="font-bold">{info.roundsRangeLabel}</span>
                  </span>
                </PracticeExamDialog>
              </div>
              {/* szerszy niż nagłówek — przy 12 px opis ma się zmieścić w dwóch linijkach;
                  górną granicę i tak trzyma kolumna (@960:max-w-[42%] / @1088:max-w-[36%]) */}
              <p className="@960:block mt-1 hidden max-w-[420px] text-xs leading-[18px] text-white">
                Sprawdź się w realnych warunkach. Przygotuj się, by podejść do
                egzaminu spokojniej i zobacz jak wypadniesz na tle innych.
              </p>
              {/* Przy wąskim banerze kroki rund nie mieszczą się obok licznika i CTA — wtedy
                  znikają, bo z całej trójki niosą najmniej treści. */}
              <PracticeExamRoundSteps
                rounds={rounds}
                currentRound={currentRound}
                className="@768:flex hidden"
              />
            </div>
            {/* opis edycji trzyma górną krawędź banera, licznik z CTA dolną — jak w projekcie */}
            <div className="@960:justify-between @960:self-stretch flex shrink-0 flex-col items-end gap-4">
              {/* Poniżej progu ten sam tekst siedzi pod nagłówkiem, więc tutaj znika w całości
                  (razem z triggerem dialogu, żeby nie zostawiał pustego odstępu).
                  pr-7 robi miejsce krzyżykowi, który siedzi w tym samym rogu. */}
              <div className="@960:block hidden pr-7">
                <PracticeExamDialog practiceExamStatus={practiceExamStatus}>
                  {/* opis edycji zawsze w jednej linii — przy zakresie z dwóch miesięcy
                      („12 czerwca – 31 lipca o 13:30") łamał się na dwie */}
                  <span className="@960:text-base cursor-pointer text-right whitespace-nowrap text-white hover:underline">
                    {metaPrefix}
                    {metaPrefix && " · "}
                    <span className="font-bold">{info.roundsRangeLabel}</span>
                  </span>
                </PracticeExamDialog>
              </div>
              <div className="@1088:gap-x-4 flex w-full flex-nowrap items-center justify-end gap-x-2">
                {showCountdown && (
                  <>
                    <span className="flex shrink-0 items-center gap-2 text-sm font-medium whitespace-nowrap text-white/80">
                      <Clock4Icon size={16} className="shrink-0 opacity-80" />
                      {isActive ? "Czas na podejście:" : "Start za:"}
                    </span>
                    <CountdownTimer
                      timeLeft={timeLeft}
                      isActive={isActive}
                      variant="practiceExam"
                      // na granatowym banerze dwukropki i podpis „dni" muszą być białe
                      className="text-white"
                    />
                  </>
                )}
                {/* shrink-0, żeby przy ciasnym banerze CTA nie zgubiło paddingu na rzecz licznika */}
                <div className="shrink-0">{renderCta()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
